import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import JobTabs from '../components/JobTabs.js';
import { indexSummaries, NewProjectSheet, ProjectGrid, summarize } from '../components/ProjectCard.js';
import { listProjects } from '../lifeos/api.js';
import { humanize } from '../lifeos/format.js';
import { useSpheres } from '../lifeos/spheres.js';
import { useLoad } from '../lifeos/useLoad.js';
import { getWorkspace, listWorkspaces, refreshWorkspaces, type ProjectSummary } from '../lib/workspaces.js';
import { Button, EmptyState, Group, Loader, MoreRow, Page, Row, useParamSetter } from '../ui/index.js';
import Icon from '../ui/Icon.js';

/** The server's SLUG_RE (spec §3.2), checked here so the form can explain itself. */
export const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,62}$/;

/** 'Big Island Villas!' → 'big-island-villas'. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

/** Every project as a summary tree, with counts from the workspace payloads when the server has them. */
async function loadOverview(): Promise<ProjectSummary[]> {
  const [items, workspaces] = await Promise.all([listProjects(), listWorkspaces().catch(() => [])]);
  const details = await Promise.allSettled(workspaces.map((w) => getWorkspace(w.key)));
  const known = new Map<string, ProjectSummary>();
  for (const d of details) if (d.status === 'fulfilled') indexSummaries(d.value.projects, known);
  return summarize(items, known);
}

/**
 * Projects (`/projects`): the overview of every workspace (spec 2026-10-14) —
 * one section per job with its project cards (sub-projects inside), the job
 * tabs on top. Paused and done projects fold away at the bottom.
 */
export default function Projects(): JSX.Element {
  const [params, setParam] = useParamSetter();
  const sphere = params.get('sphere');
  const spheres = useSpheres();
  const list = useLoad(loadOverview, []);
  const [adding, setAdding] = useState(false);
  const [showRest, setShowRest] = useState(false);

  return (
    <Page
      title="Projects"
      wide
      sub={list.data ? `${list.data.filter((p) => p.status === 'active').length} active across your workspaces` : undefined}
      actions={
        <Button icon="plus" size="sm" onClick={() => setAdding(true)}>
          Project
        </Button>
      }
    >
      <JobTabs spheres={spheres} value={sphere} onChange={(k) => setParam('sphere', k)} />
      <Loader state={list}>
        {(all) => {
          const roots = sphere ? all.filter((p) => p.sphereKey === sphere) : all;
          if (roots.length === 0)
            return (
              <EmptyState
                icon="folder"
                title="No projects yet."
                action={
                  <Button variant="secondary" icon="plus" onClick={() => setAdding(true)}>
                    Add a project
                  </Button>
                }
              />
            );
          const active = roots.filter((p) => p.status === 'active');
          const rest = roots.filter((p) => p.status !== 'active' && p.status !== 'archived');
          // Workspace order: configured sphere order, then anything unknown.
          const keys = [...spheres.map((s) => s.key), ...new Set(active.map((p) => p.sphereKey))].filter(
            (k, i, arr) => arr.indexOf(k) === i,
          );
          return (
            <>
              {keys.map((k) => {
                const mine = active.filter((p) => p.sphereKey === k);
                if (mine.length === 0) return null;
                const s = spheres.find((x) => x.key === k);
                return (
                  <section key={k} className="pv-sec" aria-label={s?.name ?? humanize(k)} style={{ '--job': s?.color ?? 'var(--text-muted)' } as CSSProperties}>
                    <div className="pv-sec__head">
                      <h2 className="pv-sec__title">
                        <span className="sb-dot" aria-hidden="true" />
                        {s?.name ?? humanize(k)}
                      </h2>
                      <Link to={`/w/${encodeURIComponent(k)}`} className="pv-sec__open">
                        Open workspace
                        <Icon name="chevron-right" size={16} />
                      </Link>
                    </div>
                    <ProjectGrid projects={mine} color={s?.color} />
                  </section>
                );
              })}
              <Group header="Paused and done" label="Other projects">
                {rest.length > 0 &&
                  (showRest ? (
                    rest.map((p) => (
                      <Row key={p.id} to={`/projects/${p.id}`} tone="muted" title={p.name} detail={humanize(p.status)} />
                    ))
                  ) : (
                    <MoreRow count={rest.length} noun={rest.length === 1 ? 'project' : 'projects'} onClick={() => setShowRest(true)} />
                  ))}
              </Group>
            </>
          );
        }}
      </Loader>
      {adding && (
        <NewProjectSheet
          spheres={spheres}
          sphereKey={sphere ?? undefined}
          onClose={() => setAdding(false)}
          onCreated={() => {
            void list.reload();
            refreshWorkspaces();
          }}
        />
      )}
    </Page>
  );
}
